import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO component — sets per-page title, meta, canonical, Open Graph, Twitter,
 * and optional JSON-LD structured data (Breadcrumbs, Product, FAQ, etc.).
 *
 * Props:
 *   title, description, keywords, canonical, image, type, noindex
 *   breadcrumbs = [{ name, url }, ...]  → renders BreadcrumbList JSON-LD
 *   structuredData = [ {...schema-org}, ... ]  → renders arbitrary JSON-LD blocks
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  image,
  type = 'website',
  noindex = false,
  breadcrumbs,
  structuredData,
}) => {
  const siteName = 'Connectify eSIMs';
  const defaultTitle = 'Connectify eSIMs | Instant Travel Data for 200+ Countries';
  const defaultDescription =
    'Get instant mobile data abroad with Connectify eSIMs. No SIM swapping, instant activation, coverage in 200+ countries. Affordable travel data plans starting from $2.99.';
  const defaultImage =
    'https://customer-assets.emergentagent.com/job_esim-connect-6/artifacts/3a8g7p2b_20571.webp';
  const siteUrl = 'https://connectify-esims.com';

  const seoTitle = title ? `${title} | ${siteName}` : defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoImage = image || defaultImage;
  const seoCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  const breadcrumbLd = Array.isArray(breadcrumbs) && breadcrumbs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.url.startsWith('http') ? b.url : `${siteUrl}${b.url}`,
        })),
      }
    : null;

  const extraLd = Array.isArray(structuredData) ? structuredData : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={seoCanonical} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoCanonical} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoCanonical} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* BreadcrumbList JSON-LD */}
      {breadcrumbLd && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbLd)}
        </script>
      )}

      {/* Any additional structured-data blocks (Product, FAQPage, etc.) */}
      {extraLd.map((block, idx) => (
        <script key={`sd-${idx}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
